import { useEffect, useMemo, useRef, useState } from 'react';
import { X, Loader2, Package, AlertCircle, MessageSquare, PauseCircle } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { formatPartySubtitle } from './partySubtitle';
import { usePOLineDetail } from './usePOLineDetail';
import { AcceptDateModal, DeclineDateModal, ProposeChangeModal } from './AcknowledgmentWorkflowModals';
import { HoldReasonModal } from './HoldReasonModal';
import POLineGRNTab from './POLineGRNTab';
import POLineASNTab from './POLineASNTab';
import POLinePricebookTab from './POLinePricebookTab';
import ShortageManagement from './ShortageManagement';
import POLineNCRTab from './POLineNCRTab';
import POLineConcessionTab from './POLineConcessionTab';
import CommunicationLog from './CommunicationLog';
import ChangeRequestsTab from './ChangeRequestsTab';
import { POLineDetailTabContent } from './POLineDetailTab';
import { POLineDetailFooter } from './POLineDetailFooter';
import OTIFOverrideButton from './OTIFOverrideButton';
import { RaiseConcessionFromLineModal } from './RaiseConcessionFromLineModal';
import { ConcessionStatusBadge } from './ConcessionStatusBadge';
import POLineHeaderBadges from './POLineHeaderBadges';
import { ncrAuditEntries } from './ncrAuditEvents';
import { canActOnProposal } from './pendingProposal';
import type { ConcessionRequestType } from '../../lib/api/types/quality';
import { useAuth } from '../../contexts/AuthContext';
import { useFormatters } from '../../contexts/PreferencesContext';
import { useUserPermissions } from '../../contexts/UserPermissionsContext';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';

interface POLineDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  lineId: number;
  userType?: 'customer' | 'supplier';
  initialAction?: 'propose_change' | null;
  initialTab?: string;
  onUpdate?: () => void;
}

export default function POLineDetailModal({ isOpen, onClose, lineId, userType = 'customer', initialAction = null, initialTab, onUpdate }: POLineDetailModalProps) {
  const { user } = useAuth();
  const { hasPurchasingWriteAccess } = useUserPermissions();
  const { isFeatureEnabled, isOpenOrdersControlHidden, isConcessionEnabledForCustomer } = useMenuSettings();
  // CR-5884: Hide the Communication tab when admin enables "Hide Communication
  // Box" and the viewer is a supplier. Customer-side viewers keep full access.
  const hideCommunicationTab = isOpenOrdersControlHidden('hide_communication_box', userType === 'supplier');
  // When the tenant uses schedule-line collaboration, PO-line-level
  // Accept / Decline / Propose are suppressed — demand lives on
  // POLineSchedule rows and negotiation belongs in the Schedule modal.
  // Keeping them visible here meant DE users could propose changes
  // against the PO-line header that never flowed back to any schedule,
  // creating two disconnected audit trails for the same logical ask.
  const scheduleCollaborationActive = isFeatureEnabled('po_line_schedules');

  // The modal's data and actions live in usePOLineDetail; what stays here is
  // presentational state only.
  const {
    line,
    changelogs,
    loading,
    error,
    grnCount,
    ncrs,
    fetchLineDetails,
    getChangeCount,
    handleToggleCritical,
    handleToggleHold,
    handleWorkflowAction,
    canShowWorkflowActions,
    canShowProposeChange,
  } = usePOLineDetail({
    lineId,
    isOpen,
    userType,
    user,
    scheduleCollaborationActive,
    hasPurchasingWriteAccess,
    onUpdate,
  });

  // Customer review 11 Sep (item 30a): NCRs join the Detail tab's Audit
  // History. Only there — getChangeCount and the Change Requests tab keep
  // reading the real change log.
  const detailAuditEntries = useMemo(
    () => (ncrs ? [...changelogs, ...ncrAuditEntries(ncrs)] : changelogs),
    [changelogs, ncrs],
  );

  // Workflow modal state
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [proposeModalOpen, setProposeModalOpen] = useState(false);
  const [holdModalOpen, setHoldModalOpen] = useState(false);
  // CR-5551: which deviation modal (if any) is open. Null = closed.
  // Same modal component handles both PE and CE, distinguished by the
  // requestType state.
  const [deviationRequestType, setDeviationRequestType] = useState<ConcessionRequestType | null>(null);
  const [modalTab, setModalTab] = useState(initialTab || 'detail');


  // CR-3400: Auto-open propose change modal when initialAction is set.
  // CR-4680 round 6: must only fire once per mount. Without the ref guard the
  // effect re-fires whenever `line` updates (e.g. fetchLineDetails reloading
  // after a cancel-change call), which re-opens the Propose Change modal
  // after the user cancels a change request. The parent unmounts this modal
  // on close (selectedLineId → null), so the ref resets naturally.
  const proposeAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (line && initialAction === 'propose_change' && !proposeAutoOpenedRef.current) {
      proposeAutoOpenedRef.current = true;
      setProposeModalOpen(true);
    }
  }, [line, initialAction]);

  const { formatCurrency: contextFormatCurrency, formatDate: contextFormatDate, formatQuantity, preferences } = useFormatters();

  // CR-4707: PO Line pop-up monetary fields (Unit Price + Supplier Proposed
  // Unit Price) consult the admin-configurable unit_price_decimals setting
  // instead of the legacy `showDecimals: true` shortcut which always meant 3.
  // Dashboards stay unaffected because they don't go through this helper.
  const formatCurrency = (value: string, currency?: string) => {
    const num = parseFloat(value);
    return contextFormatCurrency(num, currency, preferences.unit_price_decimals);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return contextFormatDate(dateString);
  };

  if (!isOpen) return null;

  // CR-7258: customer before supplier, and empty when neither is on the
  // payload -- both are optional on PurchaseOrderLine, so this can be a
  // genuinely empty string and the subtitle is then not rendered at all.
  const lineParties = formatPartySubtitle(line?.customer_name, line?.supplier_name);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-h-[95vh] flex flex-col" style={{ maxWidth: '1400px', minWidth: '1200px' }}>
        {/* Header — CR-4703: status badges only, actions moved to fixed footer */}
        <div className="flex items-center justify-between p-5 border-b bg-muted/30">
          <div className="flex items-center gap-3">
            <div className="min-w-0">
              <h2 className="text-xl font-semibold text-primary">
                PO: {line?.po_number} / Line: {line?.line_number}
              </h2>
              {/* CR-7258: name the parties here, not only on the Detail tab.
                  CR-4711 gave that tab a Customer field, but this modal has
                  ten tabs and the field is on one of them -- so opening GRN,
                  NCR or ASN/Shipments still left nothing on screen saying
                  whose order it was. The header is the only region that
                  survives a tab change, which is the same reason the PO
                  Details modal needed it. */}
              {lineParties && (
                <p
                  className="text-sm text-muted-foreground truncate"
                  title={lineParties}
                >
                  {lineParties}
                </p>
              )}
            </div>
            {/* CR-4701: % Delivered in header */}
            {line && (() => {
              const qty = parseFloat(line.quantity) || 0;
              const recv = parseFloat(line.received_qty) || 0;
              const pct = qty > 0 ? Math.min(Math.round((recv / qty) * 100), 100) : 0;
              const color = pct >= 100 ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : pct >= 50 ? 'bg-blue-100 text-blue-800 border-blue-300' : 'bg-gray-100 text-gray-800 border-gray-300';
              return (
                <span className={`px-3 py-1 rounded-full font-semibold text-xs border ${color}`}>
                  {pct}% Delivered
                </span>
              );
            })()}
            {line?.is_critical && (
              <span className="px-3 py-1 rounded-full font-semibold text-xs bg-amber-100 text-amber-800 border border-amber-300 flex items-center gap-1">
                <AlertCircle className="h-3.5 w-3.5" />
                CRITICAL
              </span>
            )}
            {line?.on_hold && (
              <span className="px-3 py-1 rounded-full font-semibold text-xs bg-red-100 text-red-800 border border-red-300 flex items-center gap-1">
                <PauseCircle className="h-3.5 w-3.5" />
                On Hold
                {(getChangeCount('on_hold') || 0) > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-red-200 text-red-900 rounded-full text-[10px]">{getChangeCount('on_hold')}</span>
                )}
              </span>
            )}
            {/* CR-4704 / CR-4859 / CR-4882: action-ownership + acknowledge
                badges, now in POLineHeaderBadges. CR-6510: the concession
                badge keeps its place between them. */}
            {line && (
              <POLineHeaderBadges line={line}>
                <ConcessionStatusBadge status={line.concession_status} />
              </POLineHeaderBadges>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-full transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-6">
              <p className="text-red-800">Error: {error}</p>
              <button onClick={() => fetchLineDetails()} className="mt-2 text-sm text-red-600 hover:text-red-800 underline">Try again</button>
            </div>
          ) : line ? (
            <Tabs
              // CR-6411: `initialTab` is caller-supplied, so a supplier-side
              // caller could land on the customer-only Pricebooks tab and get a
              // blank content area with no trigger to click back from. Fall
              // back to Detail, same guard shape as ContractPriceDetailModal.
              value={userType !== 'customer' && modalTab === 'pricebooks' ? 'detail' : modalTab}
              onValueChange={setModalTab}
              className="w-full"
            >
              <div className="border-b px-4 pt-2">
                <TabsList className="bg-transparent border-none">
                  <TabsTrigger value="detail" className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary">Detail</TabsTrigger>
                  <TabsTrigger value="change-requests" className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary">
                    <span className="flex items-center gap-1"><MessageSquare className="h-4 w-4" />Change Requests</span>
                  </TabsTrigger>
                  {/* CR-6411: Pricebooks sits immediately before ASN/Shipments.
                      Customer-side only — contract pricing is customer-managed
                      commercial data and stays hidden from suppliers (CR-5552);
                      the endpoint enforces the same rule server-side. */}
                  {userType === 'customer' && (
                    <TabsTrigger value="pricebooks" className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary">Pricebooks</TabsTrigger>
                  )}
                  <TabsTrigger value="asn" className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary">ASN/Shipments</TabsTrigger>
                  {userType === 'customer' && (
                    <TabsTrigger value="grn" className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary">GRN ({grnCount})</TabsTrigger>
                  )}
                  {/* Customer review 11 Sep (item 30b): count like GRN (n). */}
                  <TabsTrigger value="ncr" className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary">NCR{ncrs ? ` (${ncrs.length})` : ''}</TabsTrigger>
                  {/* CR-6510: the drill-down the PO-line concession badge
                      implies. Shown only when this line carries a raised
                      concession, and only where its customer runs the
                      workflow — the same per-line gate as raising one. */}
                  {line?.concession_status
                    && isConcessionEnabledForCustomer(line?.customer_id) && (
                    <TabsTrigger value="concessions" className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary">Concessions</TabsTrigger>
                  )}
                  {!hideCommunicationTab && (
                    <TabsTrigger value="communication" className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary">Communication</TabsTrigger>
                  )}
                  {userType === 'customer' && line?.shortage_qty && (
                    <TabsTrigger value="shortage" className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary">
                      <span className="flex items-center gap-2">
                        Shortage
                        {!line.shortage_resolved && <span className="bg-red-500 text-white text-xs font-semibold px-1.5 py-0.5 rounded">!</span>}
                      </span>
                    </TabsTrigger>
                  )}
                  {userType === 'supplier' && (
                    <TabsTrigger value="cfc" className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary">CFC</TabsTrigger>
                  )}
                </TabsList>
              </div>

              <TabsContent value="detail" className="m-0 p-4">
                <POLineDetailTabContent
                  line={line}
                  changelogs={detailAuditEntries}
                  userType={userType}
                  userId={user?.id}
                  formatCurrency={formatCurrency}
                  formatDate={formatDate}
                  formatQuantity={formatQuantity}
                  getChangeCount={getChangeCount}
                  canShowWorkflowActions={canShowWorkflowActions()}
                  onReviewChanges={() => setModalTab('change-requests')}
                />
              </TabsContent>

              <TabsContent value="grn" className="m-0 p-6"><POLineGRNTab lineId={lineId} orderedQty={line ? parseFloat(line.quantity) : 0} /></TabsContent>
              {userType === 'customer' && (
                <TabsContent value="pricebooks" className="m-0 p-6">
                  <POLinePricebookTab lineId={lineId} partNumber={line.part_number} />
                </TabsContent>
              )}
              <TabsContent value="asn" className="m-0 p-6"><POLineASNTab lineId={lineId} /></TabsContent>
              {line?.shortage_qty && (
                <TabsContent value="shortage" className="m-0 p-6"><ShortageManagement line={line} onUpdate={fetchLineDetails} /></TabsContent>
              )}
              <TabsContent value="cfc" className="m-0 p-6">
                <div className="text-center py-12">
                  <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">CFC (Confirmed Fixture Chart) information will be displayed here</p>
                  <p className="text-sm text-muted-foreground mt-2">Delivery confirmation and scheduling</p>
                </div>
              </TabsContent>
              <TabsContent value="ncr" className="m-0 p-6"><POLineNCRTab lineId={lineId} /></TabsContent>
              <TabsContent value="concessions" className="m-0 p-6"><POLineConcessionTab lineId={lineId} /></TabsContent>
              <TabsContent value="change-requests" className="m-0 p-6">
                <ChangeRequestsTab line={line} changelogs={changelogs} userType={userType} onAccept={() => setAcceptModalOpen(true)} onDecline={() => setDeclineModalOpen(true)} onUpdate={async () => { await fetchLineDetails(); onUpdate?.(); }} />
              </TabsContent>
              {!hideCommunicationTab && (
                <TabsContent value="communication" className="m-0 p-6">
                  <CommunicationLog lineId={lineId} poId={line.po} partNumber={line.part_number} />
                </TabsContent>
              )}
            </Tabs>
          ) : null}
        </div>

        {/* CR-6190: OTIF override control — only renders for an overridable
            line (server-gated via can_override_otif). Sits just above the
            fixed footer so it's always reachable from the line detail. */}
        {line && line.can_override_otif && (
          <div className="border-t px-4 py-2 bg-amber-50/50 flex items-center justify-end">
            <OTIFOverrideButton
              line={line}
              onDone={() => { fetchLineDetails(true); onUpdate?.(); }}
            />
          </div>
        )}

        {/* Footer — CR-4703: fixed action bar, always visible.
            CR-4705 round 2 rework: pass activeTab so the footer can hide
            the "Review Changes" button once the user is already on the
            Change Requests tab. */}
        {line && (
          <POLineDetailFooter
            line={line}
            activeTab={modalTab}
            canToggleCritical={line.status !== 'received' && userType !== 'supplier'}
            canToggleHold={line.status !== 'received' && hasPurchasingWriteAccess(line.customer_id)}
            canShowProposeChange={canShowProposeChange()}
            // Customer review 11 Sep (item 28): "Review Changes" only when
            // there is a change to review. A supplier on a `new` line was sent
            // to an empty Change Requests tab (CR-7277: nothing to show,
            // nothing to click).
            canShowWorkflowActions={canShowWorkflowActions() && canActOnProposal(line)}
            isProposer={
              line.status !== 'received' && (
                (userType === 'supplier' && line.acknowledge_status === 'change_proposed_supplier') ||
                (userType === 'customer' && line.acknowledge_status === 'change_proposed_customer')
              )
            }
            // CR-5551: only suppliers raise — customers approve. Hide on
            // closed/received lines where a new deviation makes no sense.
            // CR-6385: and only where THIS line's customer has concessions on;
            // a supplier can serve one customer that runs the workflow and one
            // that doesn't, so the switch has to be read per line.
            canRaiseDeviation={
              userType === 'supplier'
              && line.status !== 'closed'
              && line.status !== 'received'
              && isConcessionEnabledForCustomer(line.customer_id)
            }
            onRaiseConcession={() => setDeviationRequestType('concession')}
            onRequestProductionPermit={() => setDeviationRequestType('production_permit')}
            onToggleCritical={handleToggleCritical}
            onToggleHold={() => {
              if (line.on_hold) {
                handleToggleHold();
              } else {
                setHoldModalOpen(true);
              }
            }}
            onPropose={() => setProposeModalOpen(true)}
            onSwitchToChangeRequests={() => setModalTab('change-requests')}
            onClose={onClose}
          />
        )}
        {!line && (
          <div className="border-t p-4 flex justify-end gap-3 bg-muted/10">
            <button onClick={onClose} className="px-4 py-2 border border-border rounded-lg hover:bg-muted font-medium">Close</button>
          </div>
        )}
      </div>

      {/* Workflow Modals */}
      {line && (
        <>
          {/* CR-4729 rework: pass the supplier-proposed promise date so the
              modal can render "From <delivery_date> to <proposedDate>"
              instead of just the existing date. */}
          <AcceptDateModal isOpen={acceptModalOpen} onClose={() => setAcceptModalOpen(false)} lineId={line.id} lineNumber={line.line_number} partNumber={line.part_number} orderNumber={line.po_number} currentDeliveryDate={line.delivery_date} proposedDate={line.supplier_proposed_date || line.supplier_promise_date || undefined} onSubmit={handleWorkflowAction} />
          <DeclineDateModal isOpen={declineModalOpen} onClose={() => setDeclineModalOpen(false)} lineId={line.id} lineNumber={line.line_number} partNumber={line.part_number} orderNumber={line.po_number} currentDeliveryDate={line.delivery_date} proposedDate={line.supplier_proposed_date || line.supplier_promise_date || undefined} onSubmit={handleWorkflowAction} />
          <ProposeChangeModal isOpen={proposeModalOpen} onClose={() => setProposeModalOpen(false)} lineId={line.id} lineNumber={line.line_number} partNumber={line.part_number} orderNumber={line.po_number} currentDeliveryDate={line.delivery_date} currentQuantity={line.quantity} currentUnitPrice={line.unit_price} onSubmit={handleWorkflowAction} userType={userType} />
          <HoldReasonModal
            isOpen={holdModalOpen}
            onClose={() => setHoldModalOpen(false)}
            onSubmit={async (reason: string) => {
              await handleToggleHold(reason);
            }}
            lineNumber={line.line_number}
            partNumber={line.part_number}
          />
          {/* CR-5551: PE/CE modal — single component, requestType drives copy. */}
          {deviationRequestType && (
            <RaiseConcessionFromLineModal
              isOpen={true}
              onClose={() => setDeviationRequestType(null)}
              line={line}
              requestType={deviationRequestType}
              onSuccess={async () => { await fetchLineDetails(); onUpdate?.(); }}
            />
          )}
        </>
      )}
    </div>
  );
}
